// Roles & Permissions list. Sibling of AdminUserList — same Bootstrap-
// palette + bordered Paper styling. No Search/Add/Delete bar yet (the
// 3 seeded system roles are managed via PATCH only). Custom roles can
// be added in a follow-up if needed.

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Paper, Stack } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";

interface AdminRoleRow {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  can_manage_admins: boolean;
  can_manage_app_users: boolean;
  can_manage_jobs: boolean;
  can_send_push: boolean;
  can_view_reports: boolean;
}

interface ListRes {
  items: AdminRoleRow[];
}

export function RoleList() {
  const navigate = useNavigate();

  const list = useQuery<ListRes>({
    queryKey: ["admin-roles"],
    queryFn: () => apiFetch<ListRes>("/v1/admin/roles"),
  });

  const summarisePerms = (r: AdminRoleRow): string => {
    const granted = [
      r.can_manage_admins ? "Admins" : null,
      r.can_manage_app_users ? "App users" : null,
      r.can_manage_jobs ? "Jobs" : null,
      r.can_send_push ? "Push" : null,
      r.can_view_reports ? "Reports" : null,
    ].filter(Boolean) as string[];
    if (granted.length === 0) return "—";
    if (granted.length === 5) return "Full access";
    return granted.join(" · ");
  };

  const columns: DataTableColumn<AdminRoleRow>[] = [
    { field: "name", headerName: "Role", render: (r) => r.name },
    {
      field: "description",
      headerName: "Description",
      render: (r) => r.description ?? "—",
    },
    {
      field: "permissions",
      headerName: "Permissions",
      render: (r) => summarisePerms(r),
    },
    {
      field: "is_system",
      headerName: "Type",
      render: (r) => (r.is_system ? "System" : "Custom"),
    },
  ];

  // Client-side pagination model — fixed since system roles count is 3,
  // but DataTable still expects a model.
  const items = list.data?.items ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={items}
            rowCount={items.length}
            paginationModel={{ page: 0, pageSize: items.length || 10 }}
            onPaginationModelChange={() => {}}
            getRowId={(r) => r.id}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => navigate(`/app/admin-users/roles/${row.id}`)}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>
    </Box>
  );
}
